"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Select = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var helios_icons_1 = require("@carvana/helios-icons");
var helios_styles_1 = require("@carvana/helios-styles");
var test_attributes_1 = require("@carvana/test-attributes");
var react_1 = require("react");
var uuid_1 = require("uuid");
var SelectContext_1 = require("./SelectContext");
var styles_1 = require("./styles");
var useOutsideClickRef_1 = __importDefault(require("./useOutsideClickRef"));
var openKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Space'];
var navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown'];
var toReactNodeArray = function (value) {
    return (Array.isArray(value) ? value : [value]).filter(Boolean);
};
var addOrRemoveItems = function (collection, item) {
    var index = collection.indexOf(item);
    if (index === -1) {
        return __spreadArray(__spreadArray([], collection, true), [item], false);
    }
    return collection.filter(function (_, i) { return i !== index; });
};
var Select = function (_a) {
    var children = _a.children, label = _a.label, _b = _a.value, value = _b === void 0 ? '' : _b, hasError = _a.hasError, disabled = _a.disabled, className = _a.className, onChange = _a.onChange, _c = _a.search, search = _c === void 0 ? false : _c, _d = _a.multiselect, multiselect = _d === void 0 ? false : _d, _e = _a.autoFlipDirection, autoFlipDirection = _e === void 0 ? true : _e;
    var getTestAttributes = (0, test_attributes_1.useTestFunc)();
    var idPrefix = (0, react_1.useRef)((0, uuid_1.v4)());
    var _f = (0, react_1.useState)(false), isOpen = _f[0], setIsOpen = _f[1];
    var _g = (0, react_1.useState)(undefined), visualFocusId = _g[0], setVisualFocusId = _g[1];
    var outsideClickRef = (0, useOutsideClickRef_1.default)(function () { return setIsOpen(false); });
    var selectBoxRef = (0, react_1.useRef)();
    var inputBoxRef = (0, react_1.useRef)();
    var optionContainerRef = (0, react_1.useRef)();
    var allOptionsArr = react_1.Children.toArray(children);
    var _h = (0, react_1.useState)(allOptionsArr), currentOptions = _h[0], setCurrentOptions = _h[1];
    var _j = (0, react_1.useState)(toReactNodeArray(value)), selectedValues = _j[0], setSelectedValues = _j[1];
    var _k = (0, react_1.useState)(false), displayUpwards = _k[0], setDisplayUpwards = _k[1];
    var getSelectedNodes = (0, react_1.useCallback)(function () {
        return allOptionsArr
            .filter(function (x) { return selectedValues.includes(x.props.value); })
            .map(function (x) { return x.props.children; });
    }, [allOptionsArr, selectedValues, value]);
    var _l = (0, react_1.useState)(getSelectedNodes()), selectedNodes = _l[0], setSelectedNodes = _l[1];
    var buildSelectedId = function (val) { return "".concat(idPrefix.current, "-option-").concat(val.replace(' ', '_')); };
    var contextValue = (0, react_1.useMemo)(function () { return ({
        idPrefix: idPrefix.current,
        visualFocusId: visualFocusId,
        isMultiselect: !!multiselect,
        selectedValues: selectedValues,
        setSelection: function (selectedChildren, event, selectedValue) {
            var _a;
            if (multiselect) {
                var nodes = addOrRemoveItems(selectedNodes, selectedChildren);
                var values = addOrRemoveItems(selectedValues, selectedValue);
                setSelectedNodes(nodes);
                setSelectedValues(values);
                onChange(nodes, event, values);
            }
            else {
                setSelectedNodes(toReactNodeArray(selectedChildren));
                setSelectedValues(toReactNodeArray(selectedValue));
                setIsOpen(false);
                onChange(selectedChildren, event, selectedValue);
            }
            var focusId = buildSelectedId(selectedValue);
            setVisualFocusId(focusId);
            // isTrusted is true when generated by a user,
            // false when generated by the keyboard event in Select.tsx
            if (event.isTrusted) {
                (_a = selectBoxRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }
    }); }, [
        idPrefix,
        multiselect,
        onChange,
        setIsOpen,
        selectedNodes,
        selectedValues,
        value,
        visualFocusId
    ]);
    var handleClick = function () {
        var _a, _b, _c;
        if (disabled)
            return;
        if (!isOpen && !visualFocusId) {
            var focusId = (_c = (_b = (_a = optionContainerRef === null || optionContainerRef === void 0 ? void 0 : optionContainerRef.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
            setVisualFocusId(focusId);
        }
        setIsOpen(!isOpen);
    };
    var handleSearchKeypresses = function (event) {
        var targetValue = event.target.value;
        onChange(targetValue, event);
        var match = allOptionsArr.filter(function (x) {
            return x.props.children.toLowerCase().startsWith(targetValue.toLowerCase());
        });
        setCurrentOptions(match);
        setIsOpen(true);
    };
    var handleClosedKeypresses = function (event) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (openKeys.includes(event.code)) {
            setIsOpen(true);
            event.preventDefault(); // Prevent scrolling due to key press
            if (currentOptions.length === 0) {
                return;
            }
            var focusId = visualFocusId || ((_c = (_b = (_a = optionContainerRef === null || optionContainerRef === void 0 ? void 0 : optionContainerRef.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id);
            if (event.code === 'ArrowUp' || event.code === 'Home') {
                // Focus first option
                focusId = (_f = (_e = (_d = optionContainerRef === null || optionContainerRef === void 0 ? void 0 : optionContainerRef.current) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.id;
            }
            else if (event.code === 'End') {
                // Focus last option
                var options = (_g = optionContainerRef === null || optionContainerRef === void 0 ? void 0 : optionContainerRef.current) === null || _g === void 0 ? void 0 : _g.children;
                focusId = (_h = options[options.length - 1]) === null || _h === void 0 ? void 0 : _h.id;
            }
            setVisualFocusId(focusId);
        }
    };
    var handleOpenedKeypresses = function (event) {
        var _a, _b;
        if (navigationKeys.includes(event.code) || event.code === 'Space') {
            event.preventDefault(); // Prevent scrolling due to key press
        }
        if (currentOptions.length === 0) {
            return;
        }
        var options = Array.from(optionContainerRef.current.children);
        var nextIndex = options.findIndex(function (o) { return (o === null || o === void 0 ? void 0 : o.id) === visualFocusId; });
        if (event.code === 'Space' || event.code === 'Enter' || event.code === 'Tab') {
            event.preventDefault();
            // select the current highlighted value
            (_a = options[nextIndex]) === null || _a === void 0 ? void 0 : _a.click();
        }
        else if (event.code === 'Escape') {
            setIsOpen(false);
        }
        // Navigate through options
        if (event.code === 'ArrowDown') {
            nextIndex = Math.min(nextIndex + 1, options.length - 1);
        }
        else if (event.code === 'ArrowUp') {
            nextIndex = Math.max(nextIndex - 1, 0);
        }
        else if (event.code === 'Home') {
            nextIndex = 0;
        }
        else if (event.code === 'End') {
            nextIndex = options.length - 1;
        }
        else if (event.code === 'PageUp') {
            nextIndex = Math.max(0, nextIndex - 10);
        }
        else if (event.code === 'PageDown') {
            nextIndex = Math.min(options.length - 1, nextIndex + 10);
        }
        setVisualFocusId((_b = options[nextIndex]) === null || _b === void 0 ? void 0 : _b.id);
    };
    var handleKeyDown = function (event) {
        if (!isOpen) {
            handleClosedKeypresses(event);
        }
        else {
            handleOpenedKeypresses(event);
        }
    };
    var focusInput = function () { var _a; return (_a = inputBoxRef === null || inputBoxRef === void 0 ? void 0 : inputBoxRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    (0, react_1.useEffect)(function () {
        var _a;
        if (isOpen) {
            (_a = document.getElementById(visualFocusId)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'nearest' });
        }
    }, [isOpen, visualFocusId]);
    (0, react_1.useEffect)(function () {
        if (autoFlipDirection &&
            optionContainerRef.current &&
            'getBoundingClientRect' in optionContainerRef.current) {
            var rect = optionContainerRef.current.getBoundingClientRect();
            var bottomIsVisible = rect.bottom <= window.innerHeight;
            var heightOfSelectAndMargin = 56 + 8;
            var topIsVisibleWhenUp = rect.y - heightOfSelectAndMargin - rect.height >= 0;
            setDisplayUpwards(!bottomIsVisible && topIsVisibleWhenUp);
        }
    }, [isOpen]);
    var labelId = "".concat(idPrefix.current, "-label");
    var popupId = "".concat(idPrefix.current, "-popup");
    var isValueTypeString = typeof value === 'string';
    var valueComponent = (0, react_1.useMemo)(function () {
        if (search && isValueTypeString) {
            return ((0, jsx_runtime_1.jsx)(styles_1.Value, { disabled: disabled, value: value, placeholder: label, ref: inputBoxRef, role: "search", width: "100%", onChange: function (e) { return handleSearchKeypresses(e); } }));
        }
        if (isValueTypeString) {
            return (0, jsx_runtime_1.jsx)(styles_1.Value, __assign({ as: "div" }, { children: value || label }));
        }
        if (multiselect) {
            var selectedDisplayValues_1 = getSelectedNodes();
            var displayValues = (function () {
                switch (selectedDisplayValues_1.length) {
                    case 0:
                        return null;
                    case 1:
                        return selectedDisplayValues_1[0];
                    default:
                        return "".concat(selectedDisplayValues_1[selectedDisplayValues_1.length - 1], ", +").concat(selectedDisplayValues_1.length - 1, " more...");
                }
            })();
            return (0, jsx_runtime_1.jsx)(styles_1.Value, __assign({ as: "div" }, { children: displayValues || label }));
        }
        return value;
    }, [
        allOptionsArr,
        disabled,
        inputBoxRef,
        isValueTypeString,
        label,
        multiselect,
        search,
        selectedValues
    ]);
    return ((0, jsx_runtime_1.jsx)(SelectContext_1.SelectContext.Provider, __assign({ value: contextValue }, { children: (0, jsx_runtime_1.jsxs)(styles_1.SelectWrapper, __assign({ ref: outsideClickRef }, { children: [(0, jsx_runtime_1.jsxs)(styles_1.SelectBox, __assign({}, getTestAttributes('select'), { onClick: handleClick, "aria-expanded": isOpen, role: "combobox", tabIndex: disabled ? -1 : 0, "aria-labelledby": labelId, "aria-controls": popupId, "aria-haspopup": "listbox", "aria-activedescendant": visualFocusId, onKeyDown: handleKeyDown, onFocus: focusInput, ref: selectBoxRef, className: className, hasError: hasError, disabled: disabled }, { children: [(0, jsx_runtime_1.jsxs)(styles_1.InputContainer, { children: [value && ((0, jsx_runtime_1.jsx)(styles_1.LabelSmall, __assign({ id: labelId, "aria-hidden": "true", disabled: disabled }, { children: label }))), valueComponent] }), (0, jsx_runtime_1.jsx)(helios_icons_1.Caret, { direction: isOpen ? 'up' : 'down', color: disabled ? helios_styles_1.icon.disabled : helios_styles_1.icon.default })] })), currentOptions.length > 0 && ((0, jsx_runtime_1.jsx)(styles_1.OptionContainer, __assign({ hidden: !isOpen, ref: optionContainerRef, role: "listbox", id: popupId, displayUpwards: displayUpwards }, { children: currentOptions })))] })) })));
};
exports.Select = Select;
